// src/services/auth.js
// Federate OIDC Authentication — Authorization Code Flow with PKCE
// With proactive silent refresh to prevent session timeouts

const FEDERATE_AUTHORIZE = 'https://idp.federate.amazon.com/api/oauth2/v1/authorize';
// Federate client id + API base are build-time configurable (beta vs prod).
// Defaults are the prod values so a plain `npm run build` produces the prod bundle unchanged.
const CLIENT_ID = process.env.REACT_APP_FEDERATE_CLIENT_ID || 'pam-permissions-auth';
const REDIRECT_URI = window.location.origin + '/';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';

const SESSION_KEY = 'pam_auth_session';
const ATTEMPTED_KEY = 'auth_attempted';
const RETURN_URL_KEY = 'pam_auth_return_url';
const FORM_STATE_KEY = 'pam_form_state';

// Token lifetime: Federate issues ~1 hour tokens
// Refresh at 45 min to avoid expiry mid-request
const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes
const TOKEN_WARNING_BEFORE = 5 * 60 * 1000; // Show warning 5 min before expiry
const TOKEN_LIFETIME = 60 * 60 * 1000; // 1 hour (assumed)

let refreshTimer = null;
let warningTimer = null;
let onSessionWarning = null; // callback for UI warning

// ════════════════════════════════════════════
// Session management
// ════════════════════════════════════════════
const getSession = () => {
    try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (!saved) return null;
        const session = JSON.parse(saved);
        if (session.expiresAt && Date.now() > session.expiresAt) {
            sessionStorage.removeItem(SESSION_KEY);
            return null;
        }
        return session;
    } catch {
        return null;
    }
};

const setSession = (alias, accessToken) => {
    const now = Date.now();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        alias,
        accessToken: accessToken || '',
        expiresAt: now + TOKEN_LIFETIME,
        authenticatedAt: new Date().toISOString(),
        lastRefreshed: now
    }));
    // Start proactive refresh timer
    startRefreshTimer();
};

export const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEY);
    stopRefreshTimer();
};

// Get the stored access token for API calls
export const getAccessToken = () => {
    const session = getSession();
    return session?.accessToken || '';
};

// Check if token is close to expiry
export const isTokenExpiringSoon = () => {
    const session = getSession();
    if (!session || !session.expiresAt) return true;
    return (session.expiresAt - Date.now()) < TOKEN_WARNING_BEFORE;
};

// Register a callback for session warning (used by UI components)
export const onSessionExpiryWarning = (callback) => {
    onSessionWarning = callback;
};

// ════════════════════════════════════════════
// Proactive silent refresh
// ════════════════════════════════════════════
const startRefreshTimer = () => {
    stopRefreshTimer();
    
    // Proactive refresh at 45 min — save form state and redirect to Federate
    // If Midway is active, round-trip takes < 2 seconds
    warningTimer = setTimeout(() => {
        const session = getSession();
        if (!session) return; // Already expired/cleared
        if (onSessionWarning) {
            onSessionWarning('Session refreshing...');
        }
        console.log('[Auth] Proactive token refresh — redirecting to Federate...');
        // Save form state via global function
        if (window.__pamSaveFormState) {
            window.__pamSaveFormState();
        }
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
        redirectToFederate();
    }, TOKEN_REFRESH_INTERVAL);
};

const stopRefreshTimer = () => {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    if (warningTimer) { clearTimeout(warningTimer); warningTimer = null; }
};

// Silent refresh — attempt re-auth via redirect
// Since Federate doesn't support iframe-based silent refresh,
// we rely on the Midway session being active (redirect is instant, < 2s)
const silentRefresh = async () => {
    // Can't do true silent refresh with Federate (no iframe support)
    // Return false to trigger the fast redirect flow with form state preservation
    return false;
};

// ════════════════════════════════════════════
// Form state persistence (only used before forced redirect)
// ════════════════════════════════════════════
export const saveFormState = (formData) => {
    try {
        localStorage.setItem(FORM_STATE_KEY, JSON.stringify({
            data: formData,
            savedAt: Date.now(),
            path: window.location.pathname
        }));
    } catch (e) {
        console.warn('[Auth] Could not save form state:', e);
    }
};

export const loadFormState = () => {
    try {
        const saved = localStorage.getItem(FORM_STATE_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        // Only restore if saved less than 10 minutes ago
        if (Date.now() - parsed.savedAt > 10 * 60 * 1000) {
            localStorage.removeItem(FORM_STATE_KEY);
            return null;
        }
        return parsed.data;
    } catch {
        return null;
    }
};

export const clearFormState = () => {
    localStorage.removeItem(FORM_STATE_KEY);
};

// ════════════════════════════════════════════
// Retry auth on 401 (called from api.js)
// Tries silent refresh first, only redirects as last resort
// ════════════════════════════════════════════
export const handleAuthFailure = async (formDataToSave = null) => {
    console.log('[Auth] Handling auth failure — saving state and re-authenticating...');

    // ── Redirect-loop guard ──────────────────────────────────────────────
    // If we already re-authenticated very recently and STILL got a 401, another
    // redirect would loop forever (e.g. a token that logs in but can't validate).
    // In that case, stop redirecting and let the caller surface a message instead.
    const LOOP_KEY = 'pam_reauth_at';
    const lastReauth = parseInt(sessionStorage.getItem(LOOP_KEY) || '0', 10);
    if (lastReauth && (Date.now() - lastReauth) < 90 * 1000) {
        console.warn('[Auth] Re-auth already attempted <90s ago and still failing — NOT redirecting (loop guard).');
        if (onSessionWarning) onSessionWarning('Session could not be validated. Please refresh the page or sign in again.');
        return false;
    }
    sessionStorage.setItem(LOOP_KEY, String(Date.now()));

    // Save form state before redirect (so user doesn't lose work)
    // Try the global save function first (saves current React state directly)
    if (window.__pamSaveFormState) {
        window.__pamSaveFormState();
    } else if (formDataToSave) {
        saveFormState(formDataToSave);
    }
    
    // Save current URL
    sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
    
    // Clear expired session
    sessionStorage.removeItem(SESSION_KEY);
    
    // Redirect to Federate — if Midway is active, this is instant (< 2 seconds)
    redirectToFederate();
    
    // Return false — page is redirecting, caller should not retry
    return false;
};

// ════════════════════════════════════════════
// Get current user alias
// ════════════════════════════════════════════
export const getCurrentUser = async () => {
    // Skip auth on localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return localStorage.getItem('ufs_user_alias') || null;
    }

    // 1. Check existing session
    const session = getSession();
    if (session && session.alias) {
        startRefreshTimer(); // Ensure timer is running
        return session.alias;
    }

    // 2. Check if returning from Federate with auth code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
        // Clean URL immediately
        const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || window.location.pathname;
        sessionStorage.removeItem(RETURN_URL_KEY);
        window.history.replaceState({}, '', returnUrl);

        try {
            console.log('[Auth] Exchanging code...');
            const codeVerifier = sessionStorage.getItem('pkce_code_verifier') || '';
            sessionStorage.removeItem('pkce_code_verifier');
            const authResult = await exchangeCodeForAlias(code, codeVerifier);
            console.log('[Auth] Got alias:', authResult?.alias);
            if (authResult && authResult.alias) {
                setSession(authResult.alias, authResult.accessToken);
                localStorage.setItem('ufs_user_alias', authResult.alias.toLowerCase());
                sessionStorage.removeItem(ATTEMPTED_KEY);
                return authResult.alias;
            }
        } catch (err) {
            console.error('[Auth] Exchange failed:', err.message);
        }

        // Exchange failed — don't loop
        return localStorage.getItem('ufs_user_alias') || null;
    }

    // 3. Redirect to Federate (only once per page load to prevent loops)
    if (sessionStorage.getItem(ATTEMPTED_KEY)) {
        sessionStorage.removeItem(ATTEMPTED_KEY);
        return localStorage.getItem('ufs_user_alias') || null;
    }

    sessionStorage.setItem(ATTEMPTED_KEY, 'true');
    if (!sessionStorage.getItem(RETURN_URL_KEY)) {
        sessionStorage.setItem(RETURN_URL_KEY, window.location.pathname + window.location.search);
    }
    redirectToFederate();
    return null;
};

// ════════════════════════════════════════════
// Redirect to Federate
// ════════════════════════════════════════════
const redirectToFederate = () => {
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    generateCodeChallenge(codeVerifier).then(codeChallenge => {
        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            response_type: 'code',
            redirect_uri: REDIRECT_URI,
            scope: 'openid',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256'
        });
        window.location.replace(`${FEDERATE_AUTHORIZE}?${params.toString()}`);
    });
};

// ════════════════════════════════════════════
// Exchange code for alias via Lambda
// ════════════════════════════════════════════
const exchangeCodeForAlias = async (code, codeVerifier) => {
    const response = await fetch(`${API_BASE_URL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            operation: 'exchangeAuthCode',
            code,
            redirectUri: REDIRECT_URI,
            codeVerifier: codeVerifier || '',
            clientId: CLIENT_ID
        })
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Auth exchange failed: ${response.status} ${text}`);
    }

    const data = await response.json();
    const result = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    return { alias: result.alias || null, accessToken: result.accessToken || '' };
};

// ════════════════════════════════════════════
// PKCE helpers
// ════════════════════════════════════════════
const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
};

const base64UrlEncode = (buffer) => {
    return btoa(String.fromCharCode(...buffer))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};
